import { useState } from "react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useImportaTargetareContacte } from "@/hooks/api/mutate/useImportaTargetareContacte";
import type { TargetareImportRezultat } from "@/types/targetare";
import {
  imparteInLoturi,
  parseazaCsvContacte,
  type RezultatParsareCsv,
} from "@/utils/targetareCsvImport";
import { formatNumar } from "@/utils/targetareFormatting";

const MARIME_LOT = 500;

export function ImportContacteDialog() {
  const [deschis, setDeschis] = useState(false);
  const [text, setText] = useState("");
  const [parsare, setParsare] = useState<RezultatParsareCsv | null>(null);
  const [rezultat, setRezultat] = useState<TargetareImportRezultat | null>(
    null
  );
  const [eroare, setEroare] = useState<string | null>(null);
  const importa = useImportaTargetareContacte();

  const reseteaza = () => {
    setText("");
    setParsare(null);
    setRezultat(null);
    setEroare(null);
  };

  const citesteFisier = (fisier: File | undefined) => {
    if (!fisier) return;
    fisier.text().then((continut) => {
      setText(continut);
      setParsare(parseazaCsvContacte(continut));
      setRezultat(null);
    });
  };

  const analizeaza = () => {
    setParsare(parseazaCsvContacte(text));
    setRezultat(null);
    setEroare(null);
  };

  const ruleazaImport = async () => {
    if (!parsare || parsare.randuri.length === 0) return;
    setEroare(null);
    const total: TargetareImportRezultat = { importate: 0, respinse: [] };
    try {
      for (const lot of imparteInLoturi(parsare.randuri, MARIME_LOT)) {
        const r = await importa.mutateAsync({ randuri: lot });
        total.importate += r.data.importate;
        total.respinse.push(...r.data.respinse);
      }
      setRezultat(total);
    } catch (e) {
      setEroare(e instanceof Error ? e.message : "Importul a eșuat");
    }
  };

  return (
    <Dialog
      open={deschis}
      onOpenChange={(o) => {
        setDeschis(o);
        if (!o) reseteaza();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">
          <Upload className="mr-2 h-4 w-4" />
          Import contacte
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">
            Import contacte din CSV
          </DialogTitle>
          <DialogDescription>
            Coloane acceptate: <span className="font-mono">cui</span>,{" "}
            <span className="font-mono">valoare</span> (sau{" "}
            <span className="font-mono">email</span>),{" "}
            <span className="font-mono">tip</span>,{" "}
            <span className="font-mono">sursa</span>,{" "}
            <span className="font-mono">stare</span>,{" "}
            <span className="font-mono">note</span>. Separator virgulă, punct și
            virgulă sau tab; tipul se deduce din valoare dacă lipsește.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <input
            type="file"
            accept=".csv,.txt,text/csv"
            aria-label="Fișier CSV"
            className="text-sm"
            onChange={(e) => citesteFisier(e.target.files?.[0])}
          />
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={
              "cui;email;sursa;note\n160796;office@dacia.ro;linkedin;director IT"
            }
            className="min-h-[140px] font-mono text-xs"
            aria-label="Conținut CSV"
          />
          {parsare && (
            <div className="space-y-1 text-sm">
              <p>
                <span className="font-medium">
                  {formatNumar(parsare.randuri.length)}
                </span>{" "}
                rânduri valide,{" "}
                <span className="font-medium">
                  {formatNumar(parsare.erori.length)}
                </span>{" "}
                cu erori.
              </p>
              {parsare.erori.slice(0, 5).map((e) => (
                <p key={e.linie} className="text-xs text-destructive">
                  linia {e.linie}: {e.motiv}
                </p>
              ))}
            </div>
          )}
          {rezultat && (
            <div className="space-y-1 rounded-md border bg-muted/40 p-2 text-sm">
              <p>
                Importate:{" "}
                <span className="font-medium">
                  {formatNumar(rezultat.importate)}
                </span>
                , respinse:{" "}
                <span className="font-medium">
                  {formatNumar(rezultat.respinse.length)}
                </span>
              </p>
              {rezultat.respinse.slice(0, 5).map((r) => (
                <p
                  key={`${r.rand}-${r.valoare}`}
                  className="text-xs text-destructive"
                >
                  rândul {r.rand} ({r.cui} · {r.valoare}): {r.motiv}
                </p>
              ))}
            </div>
          )}
          {eroare && <p className="text-sm text-destructive">{eroare}</p>}
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={analizeaza}
            disabled={!text.trim()}
          >
            Analizează
          </Button>
          <Button
            onClick={ruleazaImport}
            disabled={
              !parsare || parsare.randuri.length === 0 || importa.isPending
            }
          >
            {importa.isPending ? "Se importă…" : "Importă"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
